import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { BookOpen, Target, TrendingUp, Calendar, LogIn } from "lucide-react";

export default function Login() {
  const { user, loading } = useAuth();
  const [processing, setProcessing] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const handleLogin = async () => {
    setProcessing(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin + "/dashboard" },
    });
    if (error) {
      console.error(error);
      setProcessing(false);
    }
    // Em caso de sucesso, o navegador é redirecionado para o Google — não há mais o que fazer aqui.
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      {/* Left - branding */}
      <div className="relative hidden md:flex flex-col justify-between bg-gradient-to-br from-tiffany-500 to-tiffany-600 text-white p-12 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-tiffany-300/30 blur-2xl" />

        <div className="relative z-10">
          <Logo size="lg" />
        </div>

        <div className="relative z-10 space-y-8 max-w-md">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-tight">
            Sua rotina de estudos, organizada em um só lugar.
          </h1>
          <p className="text-white/90 text-lg">
            Controle seu tempo, disciplinas, edital, revisões e simulados — tudo com clareza para sua aprovação.
          </p>
          <ul className="space-y-3 text-white/95">
            <li className="flex items-center gap-3"><BookOpen size={20} /> Registro completo de estudos</li>
            <li className="flex items-center gap-3"><Target size={20} /> Metas semanais e progresso do edital</li>
            <li className="flex items-center gap-3"><TrendingUp size={20} /> Estatísticas e desempenho detalhado</li>
            <li className="flex items-center gap-3"><Calendar size={20} /> Planejamento e revisões inteligentes</li>
          </ul>
        </div>

        <div className="relative z-10 text-white/70 text-sm">
          © {new Date().getFullYear()} estudei — clone educacional
        </div>
      </div>

      {/* Right - form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md space-y-8">
          <div className="md:hidden">
            <Logo size="lg" />
          </div>
          <div>
            <h2 className="font-display text-3xl font-extrabold text-foreground">Bem-vindo de volta</h2>
            <p className="text-muted-foreground mt-2">Entre para continuar seus estudos.</p>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm space-y-4">
            <Button
              onClick={handleLogin}
              disabled={processing}
              className="w-full h-12 bg-tiffany-500 hover:bg-tiffany-600 text-white font-semibold text-base"
            >
              <LogIn size={18} className="mr-2" />
              {processing ? "Redirecionando..." : "Entrar com Google"}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              Ao entrar, você concorda com nossos Termos e Política de Privacidade.
            </p>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            Novo por aqui? Basta entrar com o Google e sua conta será criada automaticamente.
          </div>
        </div>
      </div>
    </div>
  );
}
